import React, { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { X, Search, UserPlus, Check, Clock, Trash2, Users, Bell, LogIn, Copy } from "lucide-react";
import api from "../lib/api";
import { useI18n } from "../context/I18nContext";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { DEFAULT_AVATAR } from "../lib/avatars";
import { addRecent } from "../lib/recentSessions";

const av = (p) => p || DEFAULT_AVATAR;
const fmtCode = (c) => (c ? `${c.slice(0, 4)} ${c.slice(4)}` : "········");

function Row({ user, right }) {
  return (
    <div className="flex items-center gap-3 glass px-3 py-2.5">
      <div className="h-9 w-9 rounded-full bg-zinc-900 overflow-hidden shrink-0">
        <img src={av(user.picture)} alt="" className="h-full w-full object-cover" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-bold text-sm truncate">{user.nickname}</div>
        <div className="text-xs text-zinc-500 truncate">@{user.identifiant}</div>
      </div>
      <div className="shrink-0 flex items-center gap-1">{right}</div>
    </div>
  );
}

export default function SocialPanel({ open, onClose, onCountChange }) {
  const { t } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("friends");
  const [friends, setFriends] = useState([]);
  const [requests, setRequests] = useState({ incoming: [], outgoing: [] });
  const [invitations, setInvitations] = useState([]);
  const [code, setCode] = useState("");
  const [found, setFound] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [searching, setSearching] = useState(false);

  const loadAll = useCallback(async () => {
    try {
      const [f, r, i] = await Promise.all([
        api.get("/friends"), api.get("/friends/requests"), api.get("/invitations"),
      ]);
      setFriends(f.data.friends || []);
      setRequests({ incoming: r.data?.incoming || [], outgoing: r.data?.outgoing || [] });
      setInvitations(i.data.invitations || []);
      onCountChange?.((r.data?.incoming?.length || 0) + (i.data.invitations?.length || 0));
    } catch { /* silencieux */ }
  }, [onCountChange]);

  useEffect(() => {
    if (!open) return;
    loadAll();
    const iv = setInterval(() => {
      if (document.visibilityState === "visible") loadAll();
    }, 10000);
    return () => clearInterval(iv);
  }, [open, loadAll]);

  const digits = code.replace(/\D/g, "").slice(0, 8);

  const lookup = async () => {
    if (digits.length !== 8) return;
    setSearching(true); setFound(null); setNotFound(false);
    try {
      const { data } = await api.get(`/users/by-code?code=${digits}`);
      setFound(data.user);
    } catch (e) {
      if (e.response?.status === 404) setNotFound(true);
      else toast.error(e.response?.data?.detail || t("social.error"));
    } finally { setSearching(false); }
  };

  const sendRequest = async (uid) => {
    try {
      const { data } = await api.post("/friends/request", { user_id: uid });
      toast.success(data.status === "accepted" ? t("social.now_friends") : t("social.request_sent"));
      setFound((u) => (u && u.user_id === uid ? { ...u, relation: data.status === "accepted" ? "friend" : "sent" } : u));
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || t("social.error")); }
  };
  const respond = async (request_id, accept) => {
    try { await api.post("/friends/respond", { request_id, accept }); loadAll(); }
    catch { toast.error(t("social.error")); }
  };
  const cancelRequest = async (request_id) => {
    try { await api.delete(`/friends/request/${request_id}`); loadAll(); }
    catch { toast.error(t("social.error")); }
  };
  const removeFriend = async (uid) => {
    if (!window.confirm(t("social.remove_confirm"))) return;
    try { await api.delete(`/friends/${uid}`); loadAll(); }
    catch { toast.error(t("social.error")); }
  };
  const joinInvite = async (inv) => {
    try {
      if (inv.session_type === "solo") {
        addRecent("solo", inv.code);
        await api.delete(`/invitations/${inv.invitation_id}`).catch(() => {});
        onClose();
        navigate(`/solo/spectate/${inv.code}`);
      } else {
        const { data } = await api.post("/matches/join", { code: inv.code });
        addRecent("match", inv.code);
        await api.delete(`/invitations/${inv.invitation_id}`).catch(() => {});
        onClose();
        navigate(`/match/${data.match_id}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || t("social.error"));
    }
  };
  const dismissInvite = async (inv) => {
    try { await api.delete(`/invitations/${inv.invitation_id}`); loadAll(); }
    catch { toast.error(t("social.error")); }
  };

  if (!open) return null;

  const TABS = [
    ["friends", t("social.tab_friends"), friends.length],
    ["requests", t("social.tab_requests"), requests.incoming.length + invitations.length],
    ["search", t("social.tab_search"), 0],
  ];

  return createPortal(
    <div style={{ position: "fixed", inset: 0, zIndex: 70 }}>
      <div onClick={onClose} style={{ position: "absolute", inset: 0, background: "var(--bl-overlay)" }} />
      <aside
        className="absolute top-0 right-0 h-full w-full max-w-sm glass-panel border-l flex flex-col"
        style={{ animation: "none" }}
      >
        <div className="flex items-center justify-between px-4 py-4 border-b border-zinc-800">
          <h2 className="font-heading font-black text-2xl uppercase tracking-tight">{t("social.title")}</h2>
          <button onClick={onClose} className="p-2 rounded-md text-zinc-400 hover:text-white hover:bg-zinc-900"><X className="h-5 w-5" /></button>
        </div>

        <div className="flex gap-1 px-4 pt-3">
          {TABS.map(([k, label, count]) => (
            <button key={k} onClick={() => setTab(k)}
              className={`flex-1 py-2 rounded-md text-xs font-bold uppercase tracking-wide transition-colors ${tab === k ? "bg-cyan-400 text-black" : "bg-zinc-900 border border-zinc-800 text-zinc-400 hover:text-white"}`}>
              {label}{count > 0 && tab !== k ? ` (${count})` : ""}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
          {tab === "friends" && (
            friends.length === 0
              ? <p className="text-sm text-zinc-500">{t("social.no_friends")}</p>
              : friends.map((u) => (
                <Row key={u.user_id} user={u} right={
                  <button onClick={() => removeFriend(u.user_id)} title={t("social.remove")}
                    className="p-2 rounded-md text-zinc-600 hover:text-red-400 hover:bg-red-500/10"><Trash2 className="h-4 w-4" /></button>
                } />
              ))
          )}

          {tab === "requests" && (
            <>
              {invitations.length > 0 && (
                <div className="space-y-2">
                  <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 flex items-center gap-1"><Bell className="h-3 w-3" /> {t("social.invitations")}</div>
                  {invitations.map((inv) => (
                    <div key={inv.invitation_id} className="bg-cyan-400/5 border border-cyan-400/20 rounded-md px-3 py-2.5">
                      <div className="text-sm">
                        <span className="font-bold">{inv.from_nickname}</span>{" "}
                        <span className="text-zinc-400">{t("social.invited_you")}</span>
                        {inv.label ? <span className="text-zinc-500"> · {inv.label}</span> : null}
                      </div>
                      <div className="flex gap-2 mt-2">
                        <button onClick={() => joinInvite(inv)}
                          className="flex-1 inline-flex items-center justify-center gap-1.5 bg-cyan-400 text-black font-bold uppercase tracking-wide text-xs py-2 rounded-md">
                          <LogIn className="h-3.5 w-3.5" /> {t("social.join")}
                        </button>
                        <button onClick={() => dismissInvite(inv)}
                          className="px-3 rounded-md text-zinc-500 hover:text-white hover:bg-zinc-900"><X className="h-4 w-4" /></button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-500">{t("social.incoming")}</div>
              {requests.incoming.length === 0 && invitations.length === 0 && (
                <p className="text-sm text-zinc-500">{t("social.no_requests")}</p>
              )}
              {requests.incoming.map((r) => (
                <Row key={r.request_id} user={r.user} right={<>
                  <button onClick={() => respond(r.request_id, true)} title={t("social.accept")}
                    className="p-2 rounded-md text-cyan-400 hover:bg-cyan-400/10"><Check className="h-4 w-4" /></button>
                  <button onClick={() => respond(r.request_id, false)} title={t("social.decline")}
                    className="p-2 rounded-md text-zinc-600 hover:text-red-400 hover:bg-red-500/10"><X className="h-4 w-4" /></button>
                </>} />
              ))}

              {requests.outgoing.length > 0 && (
                <>
                  <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 pt-2">{t("social.outgoing")}</div>
                  {requests.outgoing.map((r) => (
                    <Row key={r.request_id} user={r.user} right={<>
                      <span className="text-xs text-zinc-500 uppercase tracking-wide px-1">{t("social.sent")}</span>
                      <button onClick={() => cancelRequest(r.request_id)} title={t("social.cancel")}
                        className="p-2 rounded-md text-zinc-600 hover:text-red-400 hover:bg-red-500/10"><X className="h-4 w-4" /></button>
                    </>} />
                  ))}
                </>
              )}
            </>
          )}

          {tab === "search" && (
            <>
              <div className="glass px-3 py-3">
                <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-500">{t("social.my_code")}</div>
                <div className="flex items-center justify-between gap-2 mt-1">
                  <span className="font-heading font-black text-2xl tracking-[0.2em] text-cyan-400 tabular-nums">{fmtCode(user?.friend_code)}</span>
                  <button onClick={() => { navigator.clipboard?.writeText(user?.friend_code || ""); toast.success(t("social.code_copied")); }}
                    className="p-2 rounded-md text-zinc-500 hover:text-white hover:bg-zinc-800"><Copy className="h-4 w-4" /></button>
                </div>
              </div>

              <div className="relative">
                <Search className="h-4 w-4 text-zinc-600 absolute left-3 top-1/2 -translate-y-1/2" />
                <input inputMode="numeric" value={code}
                  onChange={(e) => { setCode(e.target.value); setFound(null); setNotFound(false); }}
                  onKeyDown={(e) => { if (e.key === "Enter") lookup(); }}
                  placeholder={t("social.search_ph")} maxLength={9}
                  className="w-full bg-zinc-950 border border-zinc-800 focus:border-cyan-400 outline-none rounded-md pl-9 pr-3 py-2.5 text-sm tracking-[0.2em] tabular-nums" />
              </div>
              <button onClick={lookup} disabled={digits.length !== 8 || searching}
                className="w-full bg-cyan-400 text-black hover:bg-cyan-300 disabled:opacity-40 font-bold uppercase tracking-wide text-xs py-2.5 rounded-md">
                {searching ? "…" : t("social.find")}
              </button>

              {digits.length !== 8 && <p className="text-sm text-zinc-500">{t("social.search_hint")}</p>}
              {notFound && <p className="text-sm text-zinc-500">{t("social.no_results")}</p>}
              {found && (
                <Row user={found} right={
                  found.relation === "friend" ? <span className="text-xs text-cyan-400 uppercase tracking-wide px-2 flex items-center gap-1"><Users className="h-3.5 w-3.5" /> {t("social.friend")}</span>
                  : found.relation === "sent" ? <span className="text-xs text-zinc-500 uppercase tracking-wide px-2 flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {t("social.sent")}</span>
                  : <button onClick={() => sendRequest(found.user_id)}
                      className="inline-flex items-center gap-1.5 bg-cyan-400 text-black font-bold uppercase tracking-wide text-xs px-3 py-2 rounded-md">
                      <UserPlus className="h-3.5 w-3.5" /> {t("social.add")}
                    </button>
                } />
              )}
            </>
          )}
        </div>
      </aside>
    </div>,
    document.body
  );
}
